import { createContext, useEffect, useReducer } from "react";
import SuspenseLoader from "src/components/SuspenseLoader";
import axios from "axios";
import { BASE_API_URL, WEB_GTWY_API_URL } from "src/config";
import {
  apiErrorMessage,
  toastNotification,
  tokenizeData,
} from "src/utils/helper";
import {
  getKybOrgDetails,
  getOrgAccDetails,
  logoutSession,
  fetchAllOrganization,
  getAllPartnerControls,
} from "src/utils/NetworkUtils";
import { useHistory } from "react-router";
import { useQuery } from "src/hooks/querys";

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  custom_data: null,
  otp_login_user: null,
  authData: null,
  partner_data: null,
  is_local_rails_enabled: false,
};

const setAuthLocalSession = (access_token_expires_at, session_id) => {
  localStorage.setItem("access_token_expires_at", access_token_expires_at);
  localStorage.setItem("session_id", session_id);
};

const getAllOrgCustomData = async () => {
  let custom_data = {};
  const res = await fetchAllOrganization([
    { filter_key: "limit", filter_value: 1000 },
    {
      filter_key: "organization_fields",
      filter_value: "org_id,name,partner_permission_list,created_at",
    },
  ]);

  if (res.success) {
    let all_org_name_and_id = {};
    let all_org_partner_permission_and_id = {};
    res.data.forEach((org) => {
      all_org_name_and_id[org.organization.org_id] = org.organization.name;
      all_org_partner_permission_and_id[org.organization.org_id] =
        org.organization.partner_permission_list;
    });

    custom_data = {
      all_orgs: res,
      all_org_name_and_id: all_org_name_and_id,
      all_org_partner_permission_and_id: all_org_partner_permission_and_id,
    };
  }
  return custom_data;
};

const setSessionNew = (key, value) => {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INITIALISE": {
      const {
        isAuthenticated,
        user,
        partner_data,
        custom_data,
        is_local_rails_enabled,
      } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user: { ...user, is_read_only: !user?.is_admin },
        partner_data,
        custom_data,
        is_local_rails_enabled,
      };
    }
    case "LOGIN": {
      const {
        user,
        authData,
        partner_data,
        custom_data,
        is_local_rails_enabled,
      } = action.payload;
      const { is_bo_admin_login } = authData || {};
      if (is_bo_admin_login) {
        setSessionNew("is_bo_admin_login", is_bo_admin_login);
      } else {
        localStorage.removeItem("is_bo_admin_login");
      }

      return {
        ...state,
        isAuthenticated: true,
        otp_login_user: null,
        authData: authData || {},
        user: { ...user, is_read_only: !user?.is_admin },
        partner_data,
        custom_data,
        is_local_rails_enabled,
      };
    }
    case "OTP_LOGIN": {
      const { otp_login_user } = action.payload;
      return {
        ...state,
        otp_login_user,
      };
    }
    case "VERIFY_EMAIL_LOGIN": {
      const { user } = action.payload;
      return {
        ...state,
        user,
      };
    }
    case "LOGIN_FAILED": {
      const { message, user } = action.payload;

      return {
        ...state,
        login_err: message,
        user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        otp_login_user: null,
        authData: null,
        partner_data: null,
        custom_data: null,
        is_local_rails_enabled: false,
      };
    }
    case "REGISTER": {
      const { message } = action.payload;

      return {
        ...state,
        register_suc: message,
      };
    }
    case "REGISTER_FAILED": {
      const { message } = action.payload;

      return {
        ...state,
        register_err: message,
      };
    }
    case "SET_PARTNER_CONTROLS": {
      const res = action.payload;
      return {
        ...state,
        ...res,
      };
    }
    case "RESET": {
      return {
        ...state,
        register_err: null,
        login_err: null,
        register_suc: null,
        is_local_rails_enabled: false,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: "JWT",
  otp_login: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  bo_login: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const fetchAllPartnerControls = async () => {
    let partner_controls = {};
    try {
      const { data } = await getAllPartnerControls([
        { filter_key: "limit", filter_value: 10000 },
      ]);
      data.forEach((obj) => {
        if (!Object.keys(partner_controls).includes(obj.key_type)) {
          partner_controls = {
            ...partner_controls,
            [obj.key_type]: data.filter(
              (item) => item.key_type === obj.key_type
            ),
          };
        }
      });
    } catch (err) {}
    return partner_controls;
  };

  const getSpecificControls = (partner_controls) => {
    const controls_list = [
      {
        key_type: "LOCAL_RAILS",
        key_name: "is_local_rails_enabled",
        default: false,
      },
      {
        key_type: "INVEST",
        key_name: "is_invest_enabled",
        default: false,
      },
      {
        key_type: "GCA",
        key_name: "is_gca_enabled",
        default: false,
      },
    ];

    // Initialize default values for all controls
    const controls = controls_list.reduce((acc, control) => {
      acc[control.key_name] = control.default;
      return acc;
    }, {});

    // Update controls based on partner_controls input
    controls_list.forEach(({ key_type, key_name }) => {
      if (
        partner_controls[key_type] &&
        partner_controls[key_type][0].value !== undefined
      ) {
        // If the control exists in partner_controls, use its value
        controls[key_name] = partner_controls[key_type][0].value;
      }
    });

    return controls;
  };

  const getPartnerControls = async () => {
    const partner_controls = await fetchAllPartnerControls();

    const keys = getSpecificControls(partner_controls);

    dispatch({
      type: "SET_PARTNER_CONTROLS",
      payload: {
        is_local_rails_enabled: keys.is_local_rails_enabled,
        is_invest_enabled: keys.is_invest_enabled,
        is_gca_enabled: keys.is_gca_enabled,
      },
    });

    return {
      ...keys,
    };
  };

  const otp_login = async (email, password, recaptcha_token) => {
    try {
      const tokenize_password = tokenizeData(password);
      const response = await axios.post(`${WEB_GTWY_API_URL}/login`, {
        email,
        password: tokenize_password,
        "g-recaptcha-response": recaptcha_token,
      }, { withCredentials: true });
      const { success, data } = response.data;
      if (success) {
        if (data.is_email_verified === false) {
          dispatch({
            type: "VERIFY_EMAIL_LOGIN",
            payload: {
              user: { ...data },
            },
          });
          history.push("/email-verification");
        } else {
          setSessionNew(
            "password_reset_required",
            data.password_reset_required
          );
          const partner_controls = await getPartnerControls();
          dispatch({
            type: "OTP_LOGIN",
            payload: {
              otp_login_user: { ...data },
              ...partner_controls,
            },
          });
          toastNotification("success", "User Logged In Successfully", 1500);
        }
      }
    } catch (error) {
      toastNotification("error", apiErrorMessage(error), 2000);
    }
  };

  const login = async (sessionId, otp) => {
    try {
      const response = await axios.post(
        `${WEB_GTWY_API_URL}/verifyLoginOtp/${sessionId}/${otp}`,
        null,
        { withCredentials: true }
      );
      const { success, data } = response.data;
      if (success) {
        setAuthLocalSession(data.access_token_expires_at, data.session_id);
        const orgData = await getOrgAccDetails();
        if (orgData.success) {
          toastNotification("success", "User Logged In Successfully", 1500);
          try {
            const res = await getKybOrgDetails([], orgData.data.org_id);
            const { partner, organization, kyb } = res.data;
            if (res.success) {
              const custom_data = await getAllOrgCustomData();
              const partner_controls = await getPartnerControls();
              dispatch({
                type: "LOGIN",
                payload: {
                  user: { ...orgData.data },
                  authData: { ...data },
                  partner_data: {
                    partner: partner,
                    associated_org: organization,
                    kyb_status: kyb.status,
                    partner_level: partner?.partner_level || "L1",
                    error: null,
                  },
                  custom_data: custom_data,
                  ...partner_controls,
                },
              });
            }
          } catch (err) {
            dispatch({
              type: "LOGIN",
              payload: {
                user: { ...orgData.data },
                authData: { ...data },
                partner_data: {
                  partner: null,
                  associated_org: null,
                  kyb_status: null,
                  partner_level: "L1",
                  error: err,
                },
                custom_data: { all_orgs_error: err },
              },
            });
          }
        }
      }
    } catch (error) {
      toastNotification("error", apiErrorMessage(error), 2000);
    }
  };

  const bo_login = async (bo_token) => {
    try {
      const response = await axios.get(
        `${WEB_GTWY_API_URL}/partner-user/bo-admin-login?bo_admin_login_token=${bo_token}`,
        { withCredentials: true }
      );
      const { success, data } = response.data;
      if (success) {
        setAuthLocalSession(data.access_token_expires_at, data.session_id);

        const orgData = await getOrgAccDetails();
        if (orgData.success) {
          try {
            const res = await getKybOrgDetails([], orgData.data.org_id);
            const { partner, organization, kyb } = res.data;
            if (res.success) {
              const custom_data = await getAllOrgCustomData();
              const partner_controls = await getPartnerControls();
              dispatch({
                type: "LOGIN",
                payload: {
                  user: { ...orgData.data, is_admin: true },
                  authData: { ...data },
                  partner_data: {
                    partner: partner,
                    associated_org: organization,
                    kyb_status: kyb.status,
                    partner_level: partner.partner_level || "L1",
                    error: null,
                  },
                  custom_data: custom_data,
                  ...partner_controls,
                },
              });
            }
          } catch (err) {
            dispatch({
              type: "LOGIN",
              payload: {
                user: { ...orgData.data },
                authData: { ...data },
                partner_data: {
                  partner: null,
                  associated_org: null,
                  kyb_status: null,
                  partner_level: "L1",
                  error: err,
                },
                custom_data: { all_orgs_error: err },
              },
            });
          }
        }
        toastNotification("success", "BO User login successful", 1500);
      }
    } catch (error) {
      toastNotification("error", apiErrorMessage(error), 2000);
    }
  };

  const logout = async () => {
    const session_id = localStorage.getItem("session_id");
    localStorage.clear();
    try {
      await logoutSession(session_id);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "LOGOUT" });
    }
  };

  const { push } = useHistory();

  const register = async (
    first_name,
    last_name,
    email,
    org_name,
    org_country,
    password,
    phone_number,
    phone_country_code,
    recaptcha_token
  ) => {
    try {
      const tokenize_password = tokenizeData(password);
      const response = await axios.post(`${BASE_API_URL}/register`, {
        first_name,
        last_name,
        email,
        org_name,
        org_country,
        password: tokenize_password,
        phone_number,
        phone_country_code,
        is_partner_privelege_enabled: true,
      }, { withCredentials: true });
      const { success } = response.data;
      if (success) {
        toastNotification("success", "User Registered Successfully", 1500);
        return success;
      }
    } catch (error) {
      toastNotification("error", apiErrorMessage(error), 2000);
    }
  };

  const initialise = async () => {
    try {
      const orgData = await getOrgAccDetails();

      if (orgData.success) {
        const partner_controls = await getPartnerControls();
        try {
          const is_bo_login = window.localStorage.getItem("is_bo_admin_login");
          if (is_bo_login) {
            orgData.data = { ...orgData.data, is_admin: true };
          }
          const res = await getKybOrgDetails([], orgData.data.org_id);
          const { partner, organization, kyb } = res.data;
          if (res.success) {
            const custom_data = await getAllOrgCustomData();
            dispatch({
              type: "INITIALISE",
              payload: {
                isAuthenticated: true,
                user: { ...orgData.data },
                partner_data: {
                  partner: partner,
                  associated_org: organization,
                  kyb_status: kyb.status,
                  partner_level: partner.partner_level || "L1",
                  error: null,
                },
                custom_data: custom_data,
                ...partner_controls,
              },
            });
          }
        } catch (err) {
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: true,
              user: { ...orgData.data },
              partner_data: {
                partner: null,
                associated_org: null,
                kyb_status: null,
                partner_level: "L1",
                error: err,
              },
              custom_data: { all_orgs_error: err },
              ...partner_controls,
            },
          });
        }
      } else {
        localStorage.clear();
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
            user: null,
            partner_data: null,
            custom_data: null,
          },
        });
      }
    } catch (err) {
      localStorage.clear();
      dispatch({
        type: "INITIALISE",
        payload: {
          isAuthenticated: false,
          user: null,
          partner_data: null,
          custom_data: null,
        },
      });
    }
  };

  const queryParam = useQuery();
  const bo_token = queryParam.get("bo_admin_login_token");

  useEffect(() => {
    if (bo_token) {
      localStorage.clear();
      dispatch(new Event("storage"));
    }
    (async () => {
      await initialise();
    })();
  }, []);

  if (!state.isInitialised) {
    return <SuspenseLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        otp_login,
        login,
        logout,
        initialise,
        register,
        dispatch,
        bo_login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
